import {
  Alert,
  Button,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconCircleCheck, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaptureSheet } from "../../capture";
import {
  EmptyState,
  InlineError,
  LoadingSkeleton,
  MoneyField,
  RecordCard,
  SectionCard,
} from "../../components/ui";
import { api, type Customer } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";
import { METHODS, type Method } from "./methods";

/**
 * Record a payment against a bill this application never raised.
 *
 * The scenario, in the business's words: "it is possible that the collection
 * agent will pick up payments for bills that are not generated in this
 * application. They will be collecting payments from customers billed on
 * another application."
 *
 * TWO STEPS: find the customer, then the same receipt as anywhere else.
 *
 * A customer rather than a bill, because there is no bill here to find. That
 * is the whole of the difference on screen, and almost the whole of the
 * difference underneath - with one exception worth knowing while reading this
 * file:
 *
 * THERE IS NO CAP, AND THERE CANNOT BE.
 *
 * Collect payment refuses an amount larger than the bill's outstanding
 * balance, checked inside a serializable transaction. Here there is no
 * balance in this system to check against, so the amount is whatever the
 * agent types. That is why this is the one capture site in Collections that
 * demands a SIGNATURE as well as a photograph and a location - the customer's
 * mark is the only thing standing behind the figure.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO
 *
 * It does not warn when the customer also has unpaid bills IN this
 * application. The business was asked and chose not to have it. The cost is
 * worth naming where somebody will read it: a payment recorded here for a
 * bill that this system does hold leaves that bill looking unpaid forever,
 * and nothing surfaces it. The payments register is the only place it would
 * ever be spotted.
 */
export function RecordPayment() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Customer[]>();
  const [customer, setCustomer] = useState<Customer>();
  const [searching, setSearching] = useState(false);

  const [amount, setAmount] = useState<number | string>("");
  const [method, setMethod] = useState<Method>("CASH");
  const [instrument, setInstrument] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<{
    customerName: string;
    amountCollected: string;
    externalBillReference: string | null;
    manualReceiptNo: string | null;
  }>();

  const amountValue = Number(amount);
  /**
   * No upper bound from a bill, so the only ceiling is the server's own sanity
   * limit. Stated here rather than left implicit: an agent typing an extra
   * zero is the failure this screen cannot catch, and pretending otherwise
   * with a made-up maximum would be worse than admitting it.
   */
  const amountValid =
    Number.isFinite(amountValue) && amountValue >= 0.01 && amountValue <= 10_000_000;

  const chosenMethod = METHODS.find((option) => option.value === method)!;
  const instrumentNeeded = chosenMethod.instrument !== null;
  const instrumentLabel = chosenMethod.instrument ?? "";
  const instrumentValid = !instrumentNeeded || instrument.trim().length > 0;

  async function search(event: React.FormEvent) {
    event.preventDefault();
    setSearching(true);
    setError(undefined);
    try {
      // Deactivated customers are left out, which is the search endpoint's own
      // default. The server refuses one anyway - a search that hides them is a
      // convenience, and the refusal at the write is the rule.
      const found = await api.customers(query.trim());
      setResults(found.customers);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not search customers.",
      );
    } finally {
      setSearching(false);
    }
  }

  function startOver() {
    setReceipt(undefined);
    setCustomer(undefined);
    setResults(undefined);
    setQuery("");
    setAmount("");
    setInstrument("");
    setReceiptNo("");
    setExternalRef("");
    setNote("");
    setError(undefined);
  }

  if (receipt) {
    return (
      <Page title="Payment recorded">
        <Alert
          variant="light"
          color="teal"
          radius="md"
          icon={<IconCircleCheck size={20} stroke={1.8} />}
          title={`${formatINR(receipt.amountCollected)} recorded`}
        >
          <Stack gap={2}>
            <Text fz="sm">From {receipt.customerName}.</Text>
            {/* No "outstanding after" and no "settled in full". This system
                never saw the bill, so either would be a guess in the clothes
                of a fact - and this is the screen where a made-up balance
                would be believed. */}
            <Text fz="sm" c="gray.7">
              {receipt.externalBillReference
                ? `Against their bill ${receipt.externalBillReference}, raised on another system.`
                : "Against a bill raised on another system."}
            </Text>
            {receipt.manualReceiptNo && (
              <Text fz="sm" c="gray.7">
                Receipt {receipt.manualReceiptNo}.
              </Text>
            )}
          </Stack>
        </Alert>
        <Button size="md" onClick={startOver}>
          Record another
        </Button>
        <Button variant="default" size="md" onClick={() => navigate("/collections")}>
          Back to Collections
        </Button>
      </Page>
    );
  }

  return (
    <Page
      title="Record payment"
      subtitle="Against a bill raised on another system"
      back="/collections"
    >
      {!customer ? (
        <SectionCard title="Find the customer">
          <Stack gap="sm">
            <form onSubmit={search}>
              <Stack gap="sm">
                <TextInput
                  id="customer-search"
                  label="Customer name or phone"
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  placeholder="e.g. Sai Motors"
                  autoFocus
                  description="Search the customer master by name or phone number."
                  leftSection={<IconSearch size={16} stroke={1.8} />}
                />
                {error && <InlineError>{error}</InlineError>}
                <Button
                  type="submit"
                  size="md"
                  fullWidth
                  loading={searching}
                  disabled={query.trim().length < 2}
                >
                  Search
                </Button>
              </Stack>
            </form>

            {searching && <LoadingSkeleton variant="card" count={3} />}

            {results && results.length === 0 && (
              <EmptyState
                title="Nobody matched"
                description="Try part of the name, or the phone number. A customer who is not on the master has to be added by a Store Manager first."
              />
            )}

            {results && results.length > 0 && (
              <Stack gap={8}>
                {results.map((one) => (
                  <RecordCard
                    key={one.id}
                    onClick={() => {
                      setCustomer(one);
                      setError(undefined);
                    }}
                    primary={one.name}
                    secondary={one.phone ?? "no phone on record"}
                    meta={one.address ?? undefined}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </SectionCard>
      ) : (
        <>
          <SectionCard
            title="Paying"
            action={
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setCustomer(undefined);
                  setError(undefined);
                }}
              >
                Change
              </Button>
            }
          >
            <Stack gap={2}>
              <Text fz="lg" fw={600} c="gray.9">
                {customer.name}
              </Text>
              {customer.phone && (
                <Text fz="sm" c="gray.6" className="num">
                  {customer.phone}
                </Text>
              )}
              {customer.address && (
                <Text fz="sm" c="gray.6">
                  {customer.address}
                </Text>
              )}
            </Stack>
          </SectionCard>

          <CaptureSheet
            kind="EXTERNAL_PAYMENT"
            title="Proof of collection"
            description="Photo, location and the customer's signature are all required."
            submitLabel={
              amountValid ? `Record ${formatINR(amountValue)}` : "Record payment"
            }
            disabled={!amountValid || !instrumentValid}
            onSubmit={async (capture) => {
              const result = await api.recordExternalPayment({
                customerId: customer.id,
                amount: amountValue,
                paymentMethod: method,
                ...(instrument.trim() ? { instrumentNumber: instrument.trim() } : {}),
                ...(receiptNo.trim() ? { manualReceiptNo: receiptNo.trim() } : {}),
                ...(externalRef.trim()
                  ? { externalBillReference: externalRef.trim() }
                  : {}),
                ...(note.trim() ? { referenceNote: note.trim() } : {}),
                capture: {
                  ...(capture.gps ? { gps: capture.gps } : {}),
                  ...(capture.photoDataUrl ? { photoDataUrl: capture.photoDataUrl } : {}),
                  ...(capture.signatureDataUrl
                    ? { signatureDataUrl: capture.signatureDataUrl }
                    : {}),
                  ...(capture.notes ? { notes: capture.notes } : {}),
                  clientCapturedAt: capture.clientCapturedAt,
                },
              });
              setReceipt(result);
            }}
          >
            <SectionCard title="What was paid" required>
              <Stack gap="sm">
                <MoneyField
                  id="amount"
                  label="Amount collected"
                  withAsterisk
                  min={0.01}
                  value={amount}
                  onChange={setAmount}
                  error={
                    String(amount).trim() !== "" && !amountValid
                      ? "Enter the amount collected."
                      : undefined
                  }
                />

                {/* The same 2x2 grid of 42px cells as Collect payment, and the
                    same reason: four options, chosen at a customer's door,
                    often one-handed. A closed select hides three of them
                    behind a tap and makes the fourth a guess. */}
                <Stack gap={6}>
                  <Text fz="sm" fw={500} component="label" id="method-label">
                    Payment method
                  </Text>
                  <SimpleGrid
                    cols={2}
                    spacing={8}
                    role="radiogroup"
                    aria-labelledby="method-label"
                  >
                    {METHODS.map((option) => {
                      const chosen = method === option.value;
                      return (
                        <UnstyledButton
                          key={option.value}
                          role="radio"
                          aria-checked={chosen}
                          onClick={() => setMethod(option.value)}
                          h={42}
                          px={12}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "var(--mantine-radius-md)",
                            border: chosen
                              ? "2px solid #3b5bdb"
                              : "2px solid var(--mantine-color-gray-3)",
                            background: chosen
                              ? "#edf2ff"
                              : "var(--mantine-color-white)",
                            color: chosen ? "#3b5bdb" : "var(--mantine-color-gray-8)",
                            fontWeight: chosen ? 600 : 400,
                          }}
                        >
                          <Text
                            fz="sm"
                            style={{ color: "inherit", fontWeight: "inherit" }}
                          >
                            {option.label}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </SimpleGrid>
                  <Text fz={12} c="gray.6">
                    Everything except cash needs a reference — it is what
                    reconciles the receipt against the bank statement. Cash
                    goes in the cash book and needs none.
                  </Text>
                </Stack>

                {instrumentNeeded && (
                  <TextInput
                    id="instrument"
                    label={instrumentLabel}
                    withAsterisk
                    value={instrument}
                    onChange={(event) => setInstrument(event.currentTarget.value)}
                    maxLength={60}
                    description={`Recorded in the bank book. ${instrumentLabel} is what reconciles this against the statement.`}
                    error={
                      instrument.trim() === "" && instrument !== ""
                        ? `Enter the ${instrumentLabel.toLowerCase()}.`
                        : undefined
                    }
                  />
                )}

                <TextInput
                  id="receipt-no"
                  label="Receipt number"
                  value={receiptNo}
                  onChange={(event) => setReceiptNo(event.currentTarget.value)}
                  maxLength={40}
                  // One book, both kinds of receipt. The server refuses a
                  // number already used in the same register whichever screen
                  // wrote it, because the agent tears the next leaf off the
                  // same pad without caring who raised the invoice.
                  description="Optional. The number on the counterfoil you handed over — checked against both books."
                />

                <TextInput
                  id="external-ref"
                  label="Their bill number"
                  value={externalRef}
                  onChange={(event) => setExternalRef(event.currentTarget.value)}
                  maxLength={60}
                  description="Optional. The invoice number from the other system, where the customer's paperwork shows one — it is what lets this be reconciled later."
                />

                <TextInput
                  id="note"
                  label="Note"
                  description="Optional. Anything worth recording alongside the payment."
                  value={note}
                  onChange={(event) => setNote(event.currentTarget.value)}
                  maxLength={200}
                />
              </Stack>
            </SectionCard>
          </CaptureSheet>
        </>
      )}
    </Page>
  );
}
